import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
  ListResponseOfResourceType
} from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";


type SelectResourceTypeProps = {
  value?: string | null;
  onChange: (id: string | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectResourceType = ({
  value: resourceTypeId,
  onChange,
  ...rest
}: SelectResourceTypeProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [resourceType, setResourceType] = useState<IdValue | null>(null);

  // Load the initial value if we're passed a resourceTypeId
  const { isLoading, data } = useQuery<ListResponseOfResourceType>(
    ["resourceTypeById", resourceTypeId],
    () =>
      skuService.getResourceTypes(
        new ListRequestOfBaseFilter({ sortField: "value" })
      ),
    {
      enabled:
        !!resourceTypeId &&
        (!resourceType || resourceType.id !== resourceTypeId),
    }
  );

  useEffect(() => {
    if (data?.data?.[0]) {
      setResourceType({
        id: data!.data![0].id,
        value: data!.data![0].value ?? "?",
      });
    }
  }, [data, resourceType]);

  const fetchResourceTypes = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfResourceType>(
      ["resourceTypes", searchTerm],
      () =>
        skuService.getResourceTypes(
          new ListRequestOfBaseFilter({
            pageNumber: 0,
            pageSize: 20,
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
            sortField: "value",
          })
        )
    );
    // We need to massage these results into the standard format

    const resourceTypes = {
      ...result,
      data: (result.data ?? []).map(({ id, value }) => ({
        id,
        value: value ?? "?",
      })),
    };

    return { result: resourceTypes, forSearchTerm: searchTerm };
  };

  const onChangeResourceType = (resourceType: IdValue | null) => {
    setResourceType(resourceType);
    onChange(resourceType?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={resourceType}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      label={t({ id: "skuFacultyTypesTitle" })}
      fetchData={fetchResourceTypes}
      failMessage={t({ id: "skuFacultyTitle" })}
      onChange={onChangeResourceType}
    />
  );
};

export default SelectResourceType;
